/**
 * Accepting an invitation.
 *
 * The only page reachable without a session, other than login. Someone arrives
 * with a link, chooses a password, and is then sent to sign in — the password
 * they just chose is the thing that proves who they are, and issuing a session
 * straight from a redeemed link would make the link itself a credential for the
 * account's whole life if it were ever replayed before use.
 */

import { useState, type FormEvent } from 'react';

import { acceptInvite, ApiError } from './api.ts';

/** Matches the server. One rule, not two that drift. */
const MIN_PASSWORD_LENGTH = 8;

export function AcceptInvite({
  inviteCode,
  onAccepted,
}: {
  inviteCode: string;
  onAccepted: () => void;
}): React.JSX.Element {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmation) {
      // Checked here rather than on the server, which has no business receiving
      // a password twice.
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const result = await acceptInvite(inviteCode, password);
      setDone(result.email);
    } catch (err) {
      /*
       * The server answers every invitation failure identically on purpose —
       * wrong, expired and already-accepted are indistinguishable, so this cannot
       * tell them which it was either. Pretending otherwise would invent detail
       * the server deliberately withheld.
       */
      setError(err instanceof ApiError ? err.message : 'Could not accept this invitation.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="auth">
        <div className="card auth__card">
          <div className="card__body">
            <h1 className="auth__title">You are all set</h1>
            <p className="form__hint">
              Your password is saved for <strong>{done}</strong>. Sign in with it to get started.
            </p>
            <div className="form__actions">
              <button type="button" className="btn btn--primary btn--block" onClick={onAccepted}>
                Go to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <form className="card auth__card form" onSubmit={(e) => void submit(e)}>
        <div className="card__body form">
          <h1 className="auth__title">Choose a password</h1>
          <p className="form__hint">
            You have been invited to JE Protect. Pick a password only you know — nobody at JE
            Technology can see it or set it for you.
          </p>

          <div className="field">
            <label htmlFor="new-password">Password</label>
            <input
              id="new-password"
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
            {/* Length only. Composition rules push people toward predictable
                substitutions and away from passphrases. */}
            <span className="field__hint">At least {MIN_PASSWORD_LENGTH} characters.</span>
          </div>

          <div className="field">
            <label htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmation}
              autoComplete="new-password"
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </div>

          <div className="form__actions">
            <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
              {busy ? 'Saving…' : 'Set password'}
            </button>
          </div>

          {error ? (
            <p className="notice notice--error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
